/**
 * RegionResults — wraps a detail page in a tab per region holding the record,
 * matching how bulk results are grouped. A global search labels its single hit
 * with a lone tab; several hits make the tabs a switch that swaps the record.
 *
 * Failed regions are flagged rather than folded into "not found": an
 * unreachable region says nothing about whether the record is there.
 */
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Tabs from '@cloudscape-design/components/tabs';
import { ReactNode } from 'react';
import { regionLabel } from 'src/constants';
import { RegionFailure, RegionHit } from 'src/store/slices/regionFanOut';

interface RegionResultsProps {
  hits: RegionHit<unknown>[];
  activeRegion: string | null;
  searchedRegions: string[];
  regionErrors: RegionFailure[];
  onSelectRegion: (region: string) => void;
  /** Entity noun for the copy, e.g. "payment". */
  entity: string;
  /** The page body, rendered inside the active region's tab. */
  children?: ReactNode;
}

export const RegionResults = ({
  hits, activeRegion, searchedRegions, regionErrors, onSelectRegion, entity, children,
}: RegionResultsProps) => {
  const failures = regionErrors.length > 0 && (
    <Alert
      type="warning"
      header={`${regionErrors.length} of ${searchedRegions.length} regions did not respond`}
    >
      <SpaceBetween size="xxs">
        <Box variant="span">
          {hits.length > 0
            ? `This ${entity} may also exist in a region that failed.`
            : `This ${entity} was not found, but the search was incomplete.`}
        </Box>
        {regionErrors.map(e => (
          <Box key={e.region} fontSize="body-s" color="text-status-error">
            {regionLabel(e.region)}: {e.message}
          </Box>
        ))}
      </SpaceBetween>
    </Alert>
  );

  // A lookup confined to one region has no region to disambiguate.
  if (searchedRegions.length <= 1 || hits.length === 0) {
    return <SpaceBetween size="l">{failures}{children}</SpaceBetween>;
  }

  return (
    <SpaceBetween size="l">
      {failures}
      <Tabs
        activeTabId={activeRegion ?? hits[0].region}
        onChange={({ detail }) => onSelectRegion(detail.activeTabId)}
        tabs={hits.map(hit => ({
          id: hit.region,
          label: regionLabel(hit.region),
          content: children,
        }))}
      />
    </SpaceBetween>
  );
};
